import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/org-role-select.css';
import { fetchGdprOrganizations, fetchGdprRoles } from '../utils/gdprApi';
import { useTranslation } from '../utils/i18n';
import {
  normalizeGdprOrganizations,
  normalizeGdprRoles,
  saveOrganizationRoleSelection
} from '../utils/orgRoleSelection';
import { API_BASE } from '../utils/api';

const getHotelId = (hotel) => (
  hotel?.DHTHOTid ||
  hotel?.DHTHOT_id ||
  hotel?.DHTHOT_Id ||
  hotel?.hotelId ||
  hotel?.hotel_id ||
  hotel?.id
);

function OrganizationRoleSelect() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (localStorage.getItem('authSession') !== 'true') {
          setError('No authentication session found. Please log in again.');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        const orgData = await fetchGdprOrganizations();
        const roleData = await fetchGdprRoles();
        const normalizedOrganizations = normalizeGdprOrganizations(orgData);
        const normalizedRoles = normalizeGdprRoles(roleData);

        setOrganizations(normalizedOrganizations);
        setRoles(normalizedRoles);
        setSelectedOrg((currentValue) => currentValue || String(normalizedOrganizations[0]?.id || ''));
        setSelectedRole((currentValue) => currentValue || String(normalizedRoles[0]?.id || ''));

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to load organizations and roles');
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedOrg || !selectedRole) {
      setError(t('Please select both an organization and a role'));
      return;
    }

    try {
      const selection = await saveOrganizationRoleSelection({
        organizations,
        roles,
        selectedOrg,
        selectedRole
      });

      let redirectPath = '/map';

      if (selection.resolvedAppRole === 'admin') {
        try {
          const hotelsResponse = await fetch(`${API_BASE}/hotels`);

          if (hotelsResponse.ok) {
            const hotels = await hotelsResponse.json();
            const firstHotelId = Array.isArray(hotels) ? getHotelId(hotels[0]) : null;
            if (firstHotelId) {
              sessionStorage.setItem('selectedHotelId', String(firstHotelId));
              redirectPath = `/hotel/${firstHotelId}`;
            }
          }
        } catch (hotelError) {
          console.warn('Admin landing lookup failed, falling back to map:', hotelError);
        }
      }

      setSuccess(t('Selection saved successfully. Redirecting...'));
      setTimeout(() => navigate(redirectPath), 900);
    } catch (err) {
      console.error('Error saving selection:', err);
      setError(t('Failed to save your selection. Please try again.'));
    }
  };

  if (loading) {
    return (
      <div className="org-role-container">
        <div className="org-role-card">
          <h1 className="org-role-title">Drone Hotel</h1>
          <p style={{ textAlign: 'center', color: '#666' }}>{t('Loading your organizations and roles...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="org-role-container">
      <div className="org-role-card">
        <h1 className="org-role-title">Drone Hotel</h1>
        <h2 className="org-role-subtitle">{t('Select Organization & Role')}</h2>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="org-select" className="form-label org-role-label">
              {t('Organization')}
            </label>
            <select
              id="org-select"
              className="form-select org-role-select"
              value={selectedOrg}
              onChange={(e) => setSelectedOrg(e.target.value)}
              disabled={organizations.length === 0}
            >
              <option value="">{t('-- Select an Organization --')}</option>
              {organizations.map((org, index) => (
                <option key={`org-${org.id}-${index}`} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
            {organizations.length === 0 && (
              <small className="text-muted">{t('No organizations available')}</small>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="role-select" className="form-label org-role-label">
              {t('Role')}
            </label>
            <select
              id="role-select"
              className="form-select org-role-select"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              disabled={roles.length === 0}
            >
              <option value="">{t('-- Select a Role --')}</option>
              {roles.map((role, index) => (
                <option key={`role-${role.id || role.name}-${index}`} value={role.id || role.name}>
                  {role.name || role.id}
                </option>
              ))}
            </select>
            {roles.length === 0 && (
              <small className="text-muted">{t('No roles available')}</small>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100 org-role-submit"
            disabled={!selectedOrg || !selectedRole}
          >
            {t('Continue')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default OrganizationRoleSelect;
